import { useContext, useRef, useState } from "react";
import { GeaoContext } from "../../../GeaoContextProvider";
import Papa from "papaparse";
import { initialize } from "../../../../../api/onboarding";

const COLUMN_COUNT = 11;
const HEADER_ROW = ["Branch Name", "Street Address", "Street Address 2", "ZIP Code",
    "City", "State", "Contact First Name", "Contact Last Name",
    "Branch Contact Email", "Contact Role", "Branch Office Phone Number"];

// Validation configurations
const VALIDATIONS = {
    streetAddress: {
        pattern: /^[A-Za-z0-9\s'\-&./#]+$/,
        maxLength: 45,
        requiredError: "Entries Must Contain a Valid Street Address.",
        lengthError: "Street Address limited to 45 or less characters.",
        patternError: "Street Address must contain only letters, numbers, and the following special characters: ' - & . / and #."
    },
    city: {
        pattern: /^[A-Za-z\s-]+$/,
        requiredError: "Entries Must Contain a Valid City.",
        patternError: "City must contain only letters and hyphens (if applicable)."
    },
    zipCode: {
        pattern: /^\d{5}$/,
        requiredError: "Entries Must Contain a Valid Zip Code",
        patternError: "Zip Code must be 5 digits and contain only numbers."
    },
    state: {
        pattern: /^[A-Za-z]{2}$/,
        requiredError: "Entries Must Contain a Valid State Abbreviation",
        patternError: "State Abbreviation must be exactly 2 letters."
    }
};

export default function useCentersOfOperationInitializer() {
    const { agencyInterview } = useContext(GeaoContext);
    const [progress, setProgress] = useState(0);
    const errors = useRef();
    const processingState = useRef({ rowCount: 0, completedRows: 0 });

    const formatPhoneNumber = (phoneNumber) => {
        if (!phoneNumber) return "";

        // Remove extension markers (x, ext., or comma) and everything after them
        // Using indexOf to prevent ReDoS vulnerability
        const markers = ['x', 'X', 'ext.', 'EXT.', 'Ext.', ','];
        let mainNumber = phoneNumber;
        let earliestIndex = phoneNumber.length;

        for (const marker of markers) {
            const index = phoneNumber.indexOf(marker);
            if (index !== -1 && index < earliestIndex) {
                earliestIndex = index;
            }
        }

        if (earliestIndex < phoneNumber.length) {
            mainNumber = phoneNumber.substring(0, earliestIndex);
        }

        let digits = mainNumber.replace(/\D/g, '');

        // Strip leading 1 (country code) if present
        if (digits.length === 11 && digits.startsWith('1')) {
            digits = digits.slice(1);
        }

        return digits.length === 10
            ? `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
            : phoneNumber;
    };

    const validateField = (value, config, isOptional = false) => {
        if (!value) {
            return isOptional
                ? { isValid: true }
                : { isValid: false, error: config.requiredError };
        }

        if (config.maxLength && value.length > config.maxLength) {
            return { isValid: false, error: config.lengthError };
        }

        if (!config.pattern.test(value)) {
            return { isValid: false, error: config.patternError };
        }

        return { isValid: true };
    };

    const isHeaderRow = (csvRow) => {
        return HEADER_ROW.every((header, index) => csvRow[index]?.trim() === header);
    };

    const updateProgress = () => {
        processingState.current.completedRows++;
        const { completedRows, rowCount } = processingState.current;
        setProgress(Math.round((completedRows / rowCount) * 100));
    };

    const validateRow = (csvRow) => {
        const fields = [
            { index: 1, name: "Street Address", config: VALIDATIONS.streetAddress },
            { index: 2, name: "Street Address 2", config: VALIDATIONS.streetAddress, optional: true },
            { index: 3, name: "ZIP Code", config: VALIDATIONS.zipCode },
            { index: 4, name: "City", config: VALIDATIONS.city },
            { index: 5, name: "State", config: VALIDATIONS.state }
        ];

        const rowErrors = [];
        fields.forEach(({ index, name, config, optional }) => {
            let value = csvRow[index]?.trim() || "";

            // Strip periods from State fields
            if (name === "State") {
                value = value.replace(/\./g, '');
            }
            // Replace periods from City with spaces
            if (name === "City") {
                value = value.replace(/\./g, ' ');
            }

            if (name === "ZIP Code") {
                // Remove hyphen and everything after it (e.g., "12345-6789" -> "12345")
                // Using non-greedy match or indexOf to prevent ReDoS
                const dashIndex = value.trim().indexOf('-');
                value = dashIndex !== -1 ? value.trim().substring(0, dashIndex) : value.trim();
                value = value.substring(0, 5);
            }

            const validation = validateField(value, config, optional);
            if (!validation.isValid) {
                rowErrors.push({ field: name, value, message: validation.error });
            }
        });

        return rowErrors;
    };

    const validateAllRows = (rows) => {
        const validationErrors = [];
        const validRows = [];

        rows.forEach((csvRow, rowIndex) => {
            // Skip invalid rows or header rows
            if (!Array.isArray(csvRow) || csvRow.length !== COLUMN_COUNT) {
                processingState.current.rowCount--;
                return;
            }

            if (isHeaderRow(csvRow)) {
                processingState.current.rowCount--;
                return;
            }

            // Validate the row
            const rowErrors = validateRow(csvRow);

            if (rowErrors.length > 0) {
                validationErrors.push({
                    rowIndex: rowIndex + 1,
                    row: csvRow,
                    errors: rowErrors
                });
            } else {
                validRows.push({ csvRow, rowIndex });
            }
        });

        return { validationErrors, validRows };
    };

    const initializeRow = (csvRow) => {
        return initialize({
            identityType: "Agency",
            interview: {
                questionAnswers: {
                    IsBranch: { answer: "true" },
                    BranchName: { answer: csvRow[0]?.trim().toUpperCase() || "" },
                    BranchAddressLine1: { answer: csvRow[1]?.trim().toUpperCase() || "" },
                    BranchAddressLine2: { answer: csvRow[2]?.trim().toUpperCase() || "" },
                    BranchAddressZipCode: { answer: csvRow[3]?.trim().toUpperCase() || "" },
                    BranchAddressCity: { answer: csvRow[4]?.replace(/[^\w\s]/g, '').trim().toUpperCase() || "" },
                    BranchAddressState: { answer: csvRow[5]?.replace(/\./g, '').trim().toUpperCase() || "" },
                    BranchContactFirstName: { answer: csvRow[6]?.trim().toUpperCase() || "" },
                    BranchContactLastName: { answer: csvRow[7]?.trim().toUpperCase() || "" },
                    BranchContactEmail: { answer: csvRow[8]?.trim().toUpperCase() || "" },
                    BranchContactRole: { answer: csvRow[9]?.trim().toUpperCase() || "" },
                    BranchOfficePhoneNumber: { answer: formatPhoneNumber(csvRow[10]?.trim()) || "" }
                },
                relationships: [{ type: "ParentAgency", interviewId: agencyInterview?.interviewId }]
            }
        })
            .then(() => updateProgress())
            .catch(errorResponse => {
                errors.current.initializeErrors.push(errorResponse);
            });
    };

    const initializeFromCsv = (csv) => {
        setProgress(0);
        processingState.current = { rowCount: 0, completedRows: 0 };

        errors.current = {
            invalidFileType: false,
            missingAgencyInterview: false,
            initializeErrors: []
        };

        if (!csv.name.endsWith(".csv")) {
            errors.current.invalidFileType = true;
            return Promise.resolve(errors.current);
        }

        return new Promise(resolve => {
            Papa.parse(csv, {
                complete: results => resolve(results)
            });
        })
            .then(results => {
                if (results.errors.length > 0) {
                    errors.current.invalidFileType = true;
                    return errors.current;
                }

                processingState.current.rowCount = results.data.length;

                // Step 1: Validate all rows first
                const { validationErrors, validRows } = validateAllRows(results.data);

                // Step 2: If any validation errors found, return them without initializing
                if (validationErrors.length > 0) {
                    errors.current.initializeErrors = validationErrors;
                    return errors.current;
                }

                // Step 3: Check for valid session before making API calls
                if (!agencyInterview?.interviewId) {
                    errors.current.missingAgencyInterview = true;
                    return errors.current;
                }

                // Step 4: If no errors, proceed with initialize calls for all valid rows
                processingState.current.rowCount = validRows.length;
                return Promise.all(validRows.map(({ csvRow }) => initializeRow(csvRow)))
                    .then(() => errors.current);
            });
    };

    return { initializeFromCsv, progress };
}